import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Instagram, Youtube } from "lucide-react";
import heroEnv from "@/assets/hero-environment.webp";
import { useCheckout } from "@/contexts/CheckoutContext";

const ease = [0.22, 1, 0.36, 1] as const;

const headlineLines = [
  { text: "You're old enough to want more", className: "text-foreground" },
  { text: "and broke enough", className: "text-primary" },
  { text: "to still not live it.", className: "text-foreground" },
];

interface ElevatorEntryProps {
  loaded?: boolean;
}

const ElevatorEntry = ({ loaded = true }: ElevatorEntryProps) => {
  const [entered, setEntered] = useState(false);
  const [animReady, setAnimReady] = useState(false);
  const { openCheckout } = useCheckout();

  useEffect(() => {
    if (!loaded) return;
    setAnimReady(true);
    const t = setTimeout(() => setEntered(true), 50);
    return () => clearTimeout(t);
  }, [loaded]);

  const s = (i: number, critical = false) => ({
    initial: critical ? { y: 18 } : { opacity: 0, y: 18 },
    animate: entered ? (critical ? { y: 0 } : { opacity: 1, y: 0 }) : {},
    transition: { duration: 0.7, delay: critical ? 0 : 0.1 + i * 0.08, ease },
  });

  return (
    <section id="floor-00" className="relative h-screen overflow-hidden bg-background">
      {/* Background — cinematic zoom settle */}
      <motion.div
        className="absolute inset-0 z-0 will-change-transform"
        initial={{ opacity: 0, scale: 1.15 }}
        animate={entered ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 2.5, ease }}
      >
        <img
          src={heroEnv}
          alt=""
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover"
          style={{ objectPosition: "50% 45%" }}
        />
      </motion.div>

      {/* Readability overlays */}
      <div className="absolute inset-0 z-[1]" style={{ background: "radial-gradient(ellipse 80% 70% at 50% 48%, hsl(0 0% 0% / 0.6) 0%, hsl(0 0% 0% / 0.8) 50%, hsl(0 0% 0% / 0.92) 100%)" }} />
      <div className="absolute inset-0 z-[1]" style={{ background: "linear-gradient(180deg, hsl(0 0% 2% / 0.5) 0%, transparent 18%, transparent 80%, hsl(0 0% 2% / 0.98) 100%)" }} />

      {/* Vertical gate seam */}
      <motion.div
        className="absolute left-1/2 top-0 -translate-x-1/2 w-px h-full z-[2] hidden lg:block origin-top"
        style={{ background: "linear-gradient(180deg, hsl(42 80% 52% / 0.25) 0%, hsl(42 80% 52% / 0.05) 35%, transparent 100%)" }}
        initial={{ scaleY: 0, opacity: 0 }}
        animate={entered ? { scaleY: 1, opacity: 1 } : {}}
        transition={{ duration: 1.8, delay: 0.3, ease }}
      />

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col">
        {/* Nav */}
        <motion.nav {...s(0)} className="px-6 md:px-12 lg:px-20 py-4 md:py-5 flex items-center justify-between relative z-20">
          <div className="flex items-center gap-3">
            <motion.div
              className="w-1.5 h-1.5 bg-primary"
              initial={{ scale: 0, rotate: -180 }}
              animate={entered ? { scale: 1, rotate: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.6, ease }}
            />
            <span className="font-display text-[11px] font-bold tracking-[0.15em] text-foreground/90 uppercase">
              Closer Academy
            </span>
          </div>
          <motion.div
            className="flex items-center gap-4"
            initial={{ opacity: 0, x: 20 }}
            animate={entered ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.9, ease }}
          >
            <a href="https://www.instagram.com/alexkentldm?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noopener noreferrer" aria-label="Follow Closer Academy on Instagram" className="text-muted-foreground hover:text-primary transition-colors duration-300">
              <Instagram className="w-4 h-4" />
            </a>
            <a href="https://youtube.com/@alexkentldm?si=wNiymx-UqNr15QNv" target="_blank" rel="noopener noreferrer" aria-label="Subscribe to Closer Academy on YouTube" className="text-muted-foreground hover:text-primary transition-colors duration-300">
              <Youtube className="w-4 h-4" />
            </a>
            <div className="status-chip">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse-signal" />
              <span className="hidden sm:inline font-mono text-[10px]">ACCEPTING MEMBERS</span>
              <span className="sm:hidden">OPEN</span>
            </div>
          </motion.div>
        </motion.nav>

        {/* Centered hero content */}
        <div className="flex-1 flex items-center justify-center px-6 md:px-12 lg:px-20">
          <div className="w-full max-w-5xl text-center">
            {/* Headline — staggered clip-mask reveal per line */}
            <h1 className="font-display text-[1.5rem] sm:text-[2rem] md:text-[2.6rem] lg:text-[3.2rem] xl:text-[3.6rem] font-semibold leading-[1.12] tracking-[-0.03em] mb-7 md:mb-9 headline-glow">
              {headlineLines.map((line, i) => (
                <span key={i} className="overflow-hidden block">
                  {animReady ? (
                    <motion.span
                      className={`block ${line.className}`}
                      initial={{ y: "110%", opacity: 0 }}
                      animate={entered ? { y: "0%", opacity: 1 } : {}}
                      transition={{ duration: 1, delay: 0.25 + i * 0.15, ease }}
                    >
                      {line.text}
                    </motion.span>
                  ) : (
                    <span className={`block ${line.className}`}>{line.text}</span>
                  )}
                </span>
              ))}
            </h1>

            {/* Supporting copy: desktop — staggered fade */}
            <div className="hidden md:block max-w-lg mx-auto mb-9 space-y-3.5">
              <motion.p {...s(4)} className="text-foreground/80 text-[15px] leading-[1.85] font-light">
                You can only say "nah, I'm good" so many times before it turns into shame. Not because you don't want to go. Because you do.
              </motion.p>
              <motion.p {...s(5)} className="text-foreground text-base leading-[1.75] font-semibold">
                Money is still deciding too much of your life.
              </motion.p>
            </div>

            {/* Supporting copy: mobile */}
            <div className="md:hidden max-w-[320px] mx-auto mb-7 space-y-3">
              <motion.p {...s(4, true)} className="text-foreground/65 text-[14px] leading-[1.75] font-light">
                You keep saying "nah, I'm good." Not because you don't want to go. Because you do.
              </motion.p>
              <motion.p {...s(5, true)} className="text-foreground/90 text-[15px] leading-[1.7] font-semibold">
                Money is still deciding too much of your life.
              </motion.p>
            </div>

            {/* CTA module — scale-up entry */}
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={entered ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 1, delay: 0.8, ease }}
              className="max-w-[420px] mx-auto mb-7 md:mb-9"
            >
              <div className="relative border border-primary/15 overflow-hidden cta-card-hover">
                <div className="cta-card-bg" />
                <div className="relative z-10">
                  <div className="h-px w-full" style={{ background: "linear-gradient(90deg, transparent, hsl(42 80% 52% / 0.3), transparent)" }} />
                  <div className="p-5 md:p-6">
                    <div className="flex items-center justify-center gap-5 mb-4">
                      <div className="flex items-baseline gap-2.5">
                        <span className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight">$72</span>
                        <span className="text-sm md:text-base text-foreground/30 line-through font-light">$197</span>
                      </div>
                      <div className="h-6 w-px bg-border/40" />
                      <p className="text-foreground/40 text-[11px] font-light leading-relaxed text-left">
                        Built for men who are<br />done standing outside.
                      </p>
                    </div>
                    <button onClick={openCheckout} className="w-full bg-primary text-primary-foreground font-display font-bold text-xs md:text-sm tracking-[0.15em] uppercase h-12 md:h-[52px] flex items-center justify-center gap-3 cta-glow cta-btn-hover group">
                      GET INSTANT ACCESS
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-300" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Specs — staggered pop */}
            <div className="flex flex-wrap justify-center gap-1.5 md:gap-2">
              {["1-ON-1 CALLS", "PRIVATE NETWORK", "6-FIGURE FRAMEWORKS", "OFFER PLACEMENT", "AI SALES TRAINER"].map((spec, i) => (
                <motion.span
                  key={i}
                  className="spec-tag"
                  initial={{ opacity: 0, y: 12 }}
                  animate={entered ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 1 + i * 0.06, ease }}
                >
                  {spec}
                </motion.span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ElevatorEntry;
